import React, { useMemo, useImperativeHandle, forwardRef } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  GridOptions,
  ModuleRegistry,
  AllCommunityModule,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useTheme } from '../../../hooks/useTheme';
import { palette } from '../../../utils/palette';
import { InviteResponse } from '../../../api/invites';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface InvitesGridProps {
  invites: InviteResponse[];
  searchValue?: string;
}

const InvitesGrid = forwardRef<any, InvitesGridProps>(
  ({ invites, searchValue = '' }, ref) => {
    const { colors } = useTheme();
    const gridRef = React.useRef<AgGridReact>(null);
    const gridApiRef = React.useRef<any>(null);

    // Only render on web
    if (Platform.OS !== 'web') {
      return null;
    }

    // Filter invites based on search value
    const filteredInvites = React.useMemo(() => {
      if (!searchValue.trim()) return invites;
      const lowerSearch = searchValue.toLowerCase();
      return invites.filter((invite) => {
        const searchFields = [
          invite.code,
          invite.inviter_name,
          invite.inviter_email,
          invite.invitee_name,
          invite.invitee_email,
          invite.inviter_id?.toString(),
          invite.invitee_id?.toString(),
        ];
        return searchFields.some(
          (field) => field && String(field).toLowerCase().includes(lowerSearch)
        );
      });
    }, [invites, searchValue]);

    const columnDefs: ColDef[] = useMemo(
      () => [
        { field: 'id', headerName: 'ID', width: 80, sortable: true },
        {
          field: 'code',
          headerName: 'Invite Code',
          width: 150,
          sortable: true,
          filter: true,
        },
        {
          field: 'inviter_name',
          headerName: 'Inviter',
          flex: 1,
          sortable: true,
          filter: true,
          cellRenderer: (params: any) => {
            const invite = params.data;
            if (!invite.inviter_id) return '-';
            return (
              <div>
                <div>{invite.inviter_name || `ID: ${invite.inviter_id}`}</div>
                <div
                  style={{
                    color: colors.secondary,
                    fontSize: '11px',
                    marginTop: '2px',
                  }}
                >
                  {invite.inviter_email || '-'}
                </div>
              </div>
            );
          },
        },
        {
          field: 'invitee_name',
          headerName: 'Invitee',
          flex: 1,
          sortable: true,
          filter: true,
          cellRenderer: (params: any) => {
            const invite = params.data;
            if (!invite.invitee_id) {
              return <span style={{ color: colors.secondary }}>Not used</span>;
            }
            return (
              <div>
                <div>{invite.invitee_name || `ID: ${invite.invitee_id}`}</div>
                <div
                  style={{
                    color: colors.secondary,
                    fontSize: '11px',
                    marginTop: '2px',
                  }}
                >
                  {invite.invitee_email || '-'}
                </div>
              </div>
            );
          },
        },
        {
          field: 'created_at',
          headerName: 'Created',
          width: 150,
          sortable: true,
          valueFormatter: (params) => {
            if (!params.value) return '';
            return new Date(params.value).toLocaleString();
          },
        },
        {
          field: 'used_at',
          headerName: 'Used At',
          width: 150,
          sortable: true,
          valueFormatter: (params) => {
            if (!params.value) return 'Not used';
            return new Date(params.value).toLocaleString();
          },
        },
        {
          field: 'status',
          headerName: 'Status',
          width: 120,
          sortable: true,
          filter: true,
          cellRenderer: (params: any) => {
            const invite = params.data;
            const isUsed = !!invite.invitee_id;
            return (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  backgroundColor: isUsed
                    ? `${palette.cyanLimeGreen}20`
                    : `${palette.lightOrange}20`,
                  color: isUsed ? palette.cyanLimeGreen : palette.lightOrange,
                  border: `1px solid ${isUsed ? palette.cyanLimeGreen : palette.lightOrange}40`,
                  fontSize: '11px',
                  fontWeight: '600',
                  width: 'fit-content',
                }}
              >
                {isUsed ? '✓ Used' : 'Active'}
              </div>
            );
          },
        },
      ],
      [colors.secondary]
    );

    const gridOptions: GridOptions = useMemo(
      () => ({
        theme: 'legacy',
        defaultColDef: {
          resizable: true,
          sortable: true,
          filter: true,
        },
        pagination: false,
        rowHeight: 50,
        headerHeight: 40,
        suppressCellFocus: true,
        animateRows: true,
        suppressPaginationPanel: true,
        onGridReady: (params) => {
          gridApiRef.current = params.api;
        },
      }),
      []
    );

    useImperativeHandle(ref, () => ({
      getApi: () => gridRef.current?.api,
    }));

    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <div
          className="ag-theme-alpine"
          style={
            {
              height: 'calc(100vh - 200px)',
              width: '100%',
              overflow: 'hidden',
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              '--ag-header-background-color': colors.card,
              '--ag-background-color': colors.background,
              '--ag-odd-row-background-color': colors.card,
              '--ag-row-hover-color': colors.card,
              '--ag-header-foreground-color': colors.text,
              '--ag-foreground-color': colors.text,
              '--ag-border-color': colors.border,
              '--ag-secondary-border-color': colors.border,
              '--ag-input-focus-border-color': colors.primary,
              '--ag-selected-row-background-color': `${colors.primary}20`,
              '--ag-range-selection-background-color': `${colors.primary}15`,
              '--ag-range-selection-border-color': colors.primary,
              '--ag-checkbox-checked-color': colors.primary,
              '--ag-checkbox-unchecked-color': colors.border,
              '--ag-input-disabled-background-color': colors.card,
              '--ag-input-disabled-border-color': colors.border,
              '--ag-input-disabled-foreground-color': colors.secondary,
              '--ag-panel-background-color': colors.card,
              '--ag-tool-panel-background-color': colors.card,
              '--ag-side-button-selected-background-color': colors.primary,
              '--ag-side-button-selected-foreground-color': palette.white,
            } as React.CSSProperties & { [key: string]: any }
          }
        >
          <style>{`
          .ag-theme-alpine .ag-body-viewport::-webkit-scrollbar,
          .ag-theme-alpine .ag-body-horizontal-scroll::-webkit-scrollbar {
            display: none;
          }
          .ag-theme-alpine .ag-body-viewport,
          .ag-theme-alpine .ag-body-horizontal-scroll {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .ag-theme-alpine {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
            font-size: 12px;
          }
          .ag-theme-alpine .ag-header {
            border-bottom: 1px solid ${colors.border};
            font-size: 13px;
            font-weight: 600;
          }
          .ag-theme-alpine .ag-header-cell {
            border-right: 1px solid ${colors.border};
            font-size: 13px;
            font-weight: 600;
          }
          .ag-theme-alpine .ag-header-cell-text {
            font-size: 13px;
            font-weight: 600;
          }
          .ag-theme-alpine .ag-row {
            border-bottom: 1px solid ${colors.border};
            font-size: 12px;
            font-weight: 400;
          }
          .ag-theme-alpine .ag-cell {
            border-right: 1px solid ${colors.border};
            font-size: 12px;
            font-weight: 400;
            line-height: 1.5;
          }
          .ag-theme-alpine .ag-cell-value {
            font-size: 12px;
            font-weight: 400;
          }
          .ag-theme-alpine input,
          .ag-theme-alpine select,
          .ag-theme-alpine button {
            font-size: 12px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
          }
        `}</style>
          <AgGridReact
            ref={gridRef}
            rowData={filteredInvites}
            columnDefs={columnDefs}
            gridOptions={gridOptions}
            domLayout="normal"
          />
        </div>
      </View>
    );
  }
);

InvitesGrid.displayName = 'InvitesGrid';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
  },
});

export default InvitesGrid;
